import React, { useState, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, Building2, User, Mail, Lock, Users, PartyPopper,
  ArrowRight, ArrowLeft, Plus, X, Check, AlertCircle, Sun, Moon,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { toast } from "sonner";

/**
 * 5-step public signup wizard.
 *
 * 1. Shop details     (shop name, plan preview)
 * 2. Appearance       (light / dark theme pick — applies immediately
 *                      so the rest of signup previews their choice)
 * 3. Owner profile    (your name, email, password)
 * 4. Installer invites (optional, 0..25 email rows)
 * 5. Welcome / confirm
 *
 * On submit of step 5 we POST /api/public/signup → receive {token, user, tenant}.
 * The JWT is handed to AuthContext.setSession() which fetches /auth/me and
 * flips the user into the authenticated shell. We then navigate to `/` with
 * a one-time `?tour=1` query so the dashboard can trigger a cheat-sheet
 * (Phase 4 — remembered per-user).
 */
const STEPS = [
  { key: "shop",       label: "Shop",       icon: Building2 },
  { key: "appearance", label: "Appearance", icon: Sparkles },
  { key: "owner",      label: "You",        icon: User },
  { key: "team",       label: "Team",       icon: Users },
  { key: "welcome",    label: "Welcome",    icon: PartyPopper },
];

export default function SignupPage() {
  const { user, setSession } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    shop_name: "",
    plan: "starter",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    owner_password_confirm: "",
    installer_emails: [""],
  });
  const [error, setError] = useState("");

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const canNext = useMemo(() => {
    if (step === 0) return form.shop_name.trim().length >= 2;
    if (step === 1) return !!theme; // Appearance step — always valid, pre-filled
    if (step === 2) {
      return (
        form.owner_name.trim().length >= 2 &&
        /\S+@\S+\.\S+/.test(form.owner_email) &&
        form.owner_password.length >= 6 &&
        form.owner_password === form.owner_password_confirm
      );
    }
    return true;
  }, [step, form, theme]);

  if (user) return <Navigate to="/" replace />;

  const next = () => {
    setError("");
    if (!canNext) return;
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const back = () => { setError(""); if (step > 0) setStep(step - 1); };

  const submit = async () => {
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        shop_name:        form.shop_name.trim(),
        owner_name:       form.owner_name.trim(),
        owner_email:      form.owner_email.trim(),
        owner_password:   form.owner_password,
        plan:             form.plan,
        installer_emails: form.installer_emails
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e && /\S+@\S+\.\S+/.test(e)),
      };
      const { data } = await api.post("/public/signup", payload);
      await setSession(data.token, data.user);
      const invCount = data.invites_created || 0;
      if (invCount > 0) {
        toast.success(`Shop created — ${invCount} invitation${invCount === 1 ? "" : "s"} sent`);
      } else {
        toast.success(`Welcome to ${data.tenant?.name || "WrapConsole"}`);
      }
      navigate("/?tour=1", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail[0]?.msg || "Please check your details and try again."
        : detail || "Signup failed — please try again.";
      setError(msg);
      setStep(2); // most likely the email was taken; land them back on owner step
    } finally {
      setSubmitting(false);
    }
  };

  const StepIcon = STEPS[step].icon;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: "linear-gradient(135deg,#020617 0%,#0b1120 60%,#0f172a 100%)" }}
      data-testid="signup-page"
    >
      {/* Background accents */}
      <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-blue-500/20 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-emerald-500/15 blur-[120px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="glass-strong rounded-[2rem] w-full max-w-xl relative z-10 overflow-hidden"
      >
        {/* Brand header */}
        <div className="px-8 pt-8 pb-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500/40 to-emerald-500/30 border border-white/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-heading text-2xl tracking-tight text-white leading-none">WrapConsole</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Start your 14-day free trial</div>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <div className="px-8 pt-6">
          <div className="flex items-center justify-between gap-2">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              const done = i < step;
              const active = i === step;
              return (
                <React.Fragment key={s.key}>
                  <div className="flex flex-col items-center gap-1 shrink-0" data-testid={`signup-step-${s.key}`}>
                    <div
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold transition
                        ${done ? "bg-emerald-500 text-black"
                        : active ? "bg-white text-black ring-4 ring-white/10"
                        : "bg-white/5 text-slate-500 border border-white/10"}`}
                    >
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className={`text-[10px] uppercase tracking-widest ${active ? "text-white" : "text-slate-500"}`}>
                      {s.label}
                    </div>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className={`h-px flex-1 ${i < step ? "bg-emerald-500/60" : "bg-white/10"}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step body */}
        <div className="px-8 pt-8 pb-8">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
              <StepIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-white leading-tight">
                {step === 0 && "What's your shop called?"}
                {step === 1 && "Pick your look"}
                {step === 2 && "Create your account"}
                {step === 3 && "Invite your installers"}
                {step === 4 && "You're in — welcome!"}
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                {step === 0 && "This is the name that appears on quotes, invoices and customer emails."}
                {step === 1 && "Light or dark — pick what feels right. You can switch any time from the top-bar."}
                {step === 2 && "You'll be the shop admin. You can change these details later in Settings."}
                {step === 3 && "They'll get an email with a link to set their own password. Skip for now if you'd rather do it later."}
                {step === 4 && "Your 14-day free trial has started. No card required."}
              </p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-4"
            >
              {step === 0 && (
                <ShopStep form={form} update={update} />
              )}
              {step === 1 && (
                <AppearanceStep theme={theme} setTheme={setTheme} />
              )}
              {step === 2 && (
                <OwnerStep form={form} update={update} />
              )}
              {step === 3 && (
                <TeamStep form={form} update={update} />
              )}
              {step === 4 && (
                <WelcomeStep form={form} />
              )}
            </motion.div>
          </AnimatePresence>

          {error && (
            <div
              data-testid="signup-error"
              className="mt-4 flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2.5 text-red-200 text-sm"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Nav buttons */}
          <div className="mt-7 flex items-center justify-between gap-3">
            {step > 0 ? (
              <button
                onClick={back}
                data-testid="signup-back-btn"
                disabled={submitting}
                className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition disabled:opacity-50"
              >
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <Link to="/login" className="text-sm text-slate-400 hover:text-white transition" data-testid="signup-login-link">
                Already have an account? <span className="text-white">Sign in</span>
              </Link>
            )}
            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                disabled={!canNext}
                data-testid="signup-next-btn"
                className="flex items-center gap-2 bg-white text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-slate-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {step === 2 ? "Review" : "Continue"} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                data-testid="signup-finish-btn"
                className="flex items-center gap-2 bg-emerald-400 text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-emerald-300 transition disabled:opacity-60"
              >
                {submitting ? "Creating your shop…" : <>Enter my shop <ArrowRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

/* ─── Steps ──────────────────────────────────────────────────────────────── */

function ShopStep({ form, update }) {
  return (
    <>
      <TextInput
        label="Shop name"
        icon={Building2}
        value={form.shop_name}
        onChange={(v) => update({ shop_name: v })}
        placeholder="Vinyl Wrapping Australia"
        autoFocus
        testId="signup-shop-name"
      />
      <div>
        <div className="text-label block mb-2">Pick a plan (change any time)</div>
        <div className="grid grid-cols-2 gap-3">
          <PlanCard
            name="Starter"
            price="$49"
            features={["Unlimited leads & quotes", "2 installer seats", "Email + customer portal"]}
            selected={form.plan === "starter"}
            onClick={() => update({ plan: "starter" })}
            testId="signup-plan-starter"
          />
          <PlanCard
            name="Pro"
            price="$99"
            badge="Most popular"
            features={["Everything in Starter", "Unlimited installers", "Automatic AI Quoting", "Inventory + Shopify"]}
            selected={form.plan === "pro"}
            onClick={() => update({ plan: "pro" })}
            testId="signup-plan-pro"
          />
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Both plans start on a <b className="text-slate-300">14-day free trial</b>. No card required to start.
        </p>
      </div>
    </>
  );
}

function AppearanceStep({ theme, setTheme }) {
  // Simple two-tile picker. Clicking a tile flips the ThemeContext
  // immediately so the rest of the signup previews the chosen look —
  // the choice persists in localStorage so first-run after login lands
  // in the right theme without a flash.
  const options = [
    {
      value: "dark",
      label: "Dark",
      copy: "Default — easier on the eyes in a workshop or at night.",
      icon: Moon,
      preview: {
        card: "bg-slate-900 border-slate-700",
        title: "text-white",
        sub: "text-slate-400",
        chip: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
      },
    },
    {
      value: "light",
      label: "Light",
      copy: "Brighter, print-friendly, nice for daytime office use.",
      icon: Sun,
      preview: {
        card: "bg-white border-slate-200",
        title: "text-slate-900",
        sub: "text-slate-500",
        chip: "bg-emerald-100 text-emerald-700 border-emerald-300",
      },
    },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="signup-appearance">
      {options.map((opt) => {
        const Icon = opt.icon;
        const selected = theme === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => setTheme(opt.value)}
            data-testid={`signup-theme-${opt.value}`}
            aria-pressed={selected}
            className={`relative text-left rounded-2xl border p-4 transition group overflow-hidden ${
              selected
                ? "border-emerald-400/60 bg-white/[0.04] ring-2 ring-emerald-400/30"
                : "border-white/10 bg-white/[0.02] hover:border-white/20"
            }`}
          >
            {/* Mini preview card so the shop can SEE what each theme
                looks like before committing — much more useful than a
                plain radio-button list. */}
            <div className={`rounded-xl border ${opt.preview.card} p-3 mb-3 shadow-inner`}>
              <div className="flex items-center justify-between">
                <div className={`text-xs font-semibold ${opt.preview.title}`}>Preview</div>
                <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${opt.preview.chip}`}>
                  Wrapping
                </span>
              </div>
              <div className={`text-sm font-medium mt-1.5 ${opt.preview.title}`}>Full satin wrap</div>
              <div className={`text-[11px] mt-0.5 ${opt.preview.sub}`}>2023 Tesla Model 3</div>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
                selected ? "bg-emerald-500 text-black" : "bg-white/10 text-slate-300"
              }`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-white text-sm font-medium flex items-center gap-2">
                  {opt.label}
                  {selected && <Check className="w-3.5 h-3.5 text-emerald-400" />}
                </div>
                <div className="text-slate-400 text-xs line-clamp-2">{opt.copy}</div>
              </div>
            </div>
          </button>
        );
      })}
      <p className="col-span-full text-[11px] text-slate-500 mt-1">
        Changing your mind later? Use the sun/moon toggle at the top of
        the app — your choice is saved per device.
      </p>
    </div>
  );
}


function OwnerStep({ form, update }) {
  const mismatch =
    form.owner_password && form.owner_password_confirm &&
    form.owner_password !== form.owner_password_confirm;
  return (
    <>
      <TextInput
        label="Your full name"
        icon={User}
        value={form.owner_name}
        onChange={(v) => update({ owner_name: v })}
        placeholder="Alex Nguyen"
        autoFocus
        testId="signup-owner-name"
      />
      <TextInput
        label="Email"
        icon={Mail}
        type="email"
        value={form.owner_email}
        onChange={(v) => update({ owner_email: v })}
        placeholder="you@example.com"
        testId="signup-owner-email"
      />
      <TextInput
        label="Password"
        icon={Lock}
        type="password"
        value={form.owner_password}
        onChange={(v) => update({ owner_password: v })}
        placeholder="At least 6 characters"
        testId="signup-owner-password"
      />
      <TextInput
        label="Confirm password"
        icon={Lock}
        type="password"
        value={form.owner_password_confirm}
        onChange={(v) => update({ owner_password_confirm: v })}
        placeholder="Type it again"
        testId="signup-owner-password-confirm"
      />
      {mismatch && (
        <div className="text-xs text-amber-300">Passwords don't match yet.</div>
      )}
    </>
  );
}

function TeamStep({ form, update }) {
  const emails = form.installer_emails.length === 0 ? [""] : form.installer_emails;

  const setAt = (i, val) => {
    const next = [...emails];
    next[i] = val;
    update({ installer_emails: next });
  };
  const add = () => update({ installer_emails: [...emails, ""] });
  const remove = (i) => {
    const next = emails.filter((_, idx) => idx !== i);
    update({ installer_emails: next.length === 0 ? [""] : next });
  };

  return (
    <>
      <div className="space-y-2.5">
        {emails.map((em, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`signup-installer-row-${i}`}>
            <div className="flex-1 relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="email"
                value={em}
                onChange={(e) => setAt(i, e.target.value)}
                placeholder="installer@example.com"
                data-testid={`signup-installer-email-${i}`}
                className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
            </div>
            {emails.length > 1 && (
              <button
                onClick={() => remove(i)}
                data-testid={`signup-installer-remove-${i}`}
                className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-slate-400 hover:text-red-300"
                aria-label="Remove invitee"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        onClick={add}
        data-testid="signup-installer-add"
        className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition"
      >
        <Plus className="w-3.5 h-3.5" /> Add another installer
      </button>
      <p className="text-[11px] text-slate-500">
        Not sure yet? Just skip — you can send invitations from Settings → Accounts any time.
      </p>
    </>
  );
}

function WelcomeStep({ form }) {
  const validInvites = form.installer_emails.filter(
    (e) => e && /\S+@\S+\.\S+/.test(e.trim())
  );
  return (
    <div className="space-y-4">
      <SummaryRow label="Shop" value={form.shop_name} />
      <SummaryRow label="Plan"
        value={<>
          {form.plan === "pro" ? "Pro" : "Starter"}
          <span className="text-[10px] text-emerald-300 ml-2 uppercase tracking-widest">14-day free trial</span>
        </>}
      />
      <SummaryRow label="Owner" value={`${form.owner_name} · ${form.owner_email}`} />
      <SummaryRow
        label="Invites"
        value={validInvites.length === 0
          ? "None yet (you can invite later)"
          : `${validInvites.length} installer${validInvites.length === 1 ? "" : "s"}`}
      />
      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 text-xs text-emerald-100">
        You'll land straight on your dashboard. No card is required until your trial ends in 14 days.
      </div>
    </div>
  );
}

/* ─── UI atoms ───────────────────────────────────────────────────────────── */

function TextInput({ label, icon: Icon, value, onChange, type = "text", placeholder, autoFocus, testId }) {
  return (
    <div>
      <label className="text-label block mb-2">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          data-testid={testId}
          className={`w-full bg-black/40 border border-white/10 rounded-xl py-3 text-white placeholder-slate-500 focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20 ${Icon ? "pl-10 pr-4" : "px-4"}`}
        />
      </div>
    </div>
  );
}

function PlanCard({ name, price, features, selected, onClick, badge, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`relative text-left rounded-2xl p-4 border transition
        ${selected
          ? "border-white bg-white/10 ring-2 ring-white/20"
          : "border-white/10 bg-black/30 hover:border-white/30"}`}
    >
      {badge && (
        <span className="absolute -top-2 right-3 bg-emerald-400 text-black text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
      <div className="flex items-baseline justify-between">
        <div className="font-heading text-lg text-white">{name}</div>
        <div className="text-right">
          <div className="text-white font-semibold">{price}</div>
          <div className="text-[9px] text-slate-500 uppercase tracking-widest">AUD/mo</div>
        </div>
      </div>
      <ul className="mt-2 space-y-1 text-[11px] text-slate-300">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-1.5">
            <Check className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /> {f}
          </li>
        ))}
      </ul>
    </button>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-sm text-white text-right">{value}</div>
    </div>
  );
}
